import BigNumber from 'bignumber.js';
import _ from 'lodash';

import { EnumUtility, FormatUtility } from '@codequality/vue-cli-base';

import { E入力分類, E千円表示フラグ, E端数処理, E経理方式, Eフェーズ種別 } from '@/openapi/enum';
import {
  CデータPlbscfItem,
  データListItemResponse,
  勘定科目ListItemResponse,
  帳票ListItemRequest,
  計画書データListItemResponse,
  部門ListItemResponse,
} from '@/openapi/user';

import StorageDao, { 事業所 } from '@/classes/common/StorageDao';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import AppFormatUtility from '@/classes/common/AppFormatUtility';

export type 帳票データ = Omit<帳票ListItemRequest, 'templateName'>;

export enum ServiceType {
  分析Plbscf,
  金融機関借入,
  役員借入,
  法人保有不動産,
  償却,
  実態BS,
  計画Plbscf,
  返済計画,
  返済明細,
  納税予測,
  資金繰り,
  実態純資産,
  個人保有不動産,
  差異分析,
  業績予測,
  OldLoan,
}

export default abstract class AbstractデータService<T> {
  protected static 事業所 = StorageDao.get事業所();

  protected static 計画書 = StorageDao.get計画書();

  protected abstract serviceType: ServiceType;

  protected eフェーズ種別: Eフェーズ種別 | null;

  protected 勘定科目s: 勘定科目ListItemResponse[] = [];

  protected 部門s: 部門ListItemResponse[] = [];

  /** BigNumber の端数処理モード */
  protected bnRoundingMode: {
    減価償却: BigNumber.RoundingMode;
    消費税: BigNumber.RoundingMode;
    実績計画: BigNumber.RoundingMode;
  } = {
    減価償却: BigNumber.ROUND_HALF_UP,
    消費税: BigNumber.ROUND_HALF_UP,
    実績計画: BigNumber.ROUND_HALF_UP,
  };

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------
  /**
   * フェーズ種別で処理を分岐させる必要がある場合に引数を渡す想定
   *
   * @param eフェーズ種別
   */
  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    this.eフェーズ種別 = eフェーズ種別;
  }

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------
  /**
   * getter
   */
  public get get勘定科目s(): 勘定科目ListItemResponse[] {
    return this.勘定科目s;
  }
  public get get部門s(): 部門ListItemResponse[] {
    return this.部門s;
  }
  /**
   * setter
   */
  public static set set事業所(事業所: 事業所) {
    this.事業所 = 事業所;
  }
  public static set set計画書(計画書: 計画書データListItemResponse) {
    this.計画書 = 計画書;
  }
  public set set勘定科目s(勘定科目s: 勘定科目ListItemResponse[]) {
    this.勘定科目s = 勘定科目s;
  }
  public set set部門s(部門s: 部門ListItemResponse[]) {
    this.部門s = 部門s;
  }

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------
  /**
   * 経理方式Labelを取得します
   * ※税抜／税込
   *
   * @returns
   */
  public static get経理方式Label(): string {
    return EnumUtility.valueToKey(E経理方式, this.事業所.c経理方式).substring(0, 2);
  }

  /**
   * 単位ラベルを取得します
   * ※（単位：千円、税抜）等
   *
   * @returns
   */
  public static getMoneyUnitLabel(): string {
    const moneyUnit = EnumUtility.valueToKey(E千円表示フラグ, this.計画書.c千円表示フラグ);
    return `（単位：${moneyUnit}、${this.get経理方式Label()}）`;
  }

  /**
   * 円の単位を返却します
   *
   * @param e千円表示フラグ
   * @returns
   */
  public static getMoneyUnit(e千円表示フラグ: E千円表示フラグ): number | null {
    return e千円表示フラグ === E千円表示フラグ.円 ? null : 1000;
  }

  /**
   * 年の値を月割り計算します(余りは最後の月に加算)
   *
   * @param item
   * @param yearlyValue
   */
  public static divideYearlyValue(
    item: Partial<Pick<CデータPlbscfItem, 'value' | 'monthlyValues'>>,
    yearlyValue: number
  ) {
    item.value = yearlyValue;
    item.monthlyValues = this.calculate月割りValues(yearlyValue);
  }

  /**
   * 月割りの値を計算(余りは最後の月に加算)
   *
   * @param yearlyValue
   * @returns
   */
  public static calculate月割りValues(yearlyValue: number): number[] {
    const values = new Array(MONTHS_OF_YEAR - 1).fill(Math.floor(yearlyValue / MONTHS_OF_YEAR));
    return [...values, yearlyValue - _.sum(values)];
  }

  /**
   * valueを引数の割合で換算し、指定の小数点桁数・端数処理モードで丸めた値を返します
   * (value × numerator / denominator)
   *
   * @param value
   * @param numerator
   * @param denominator
   * @param roundingMode
   * @param decimalPlaces
   * @returns
   */
  public static calcPercentByBigNumber(
    value: number,
    numerator: number,
    denominator: number,
    roundingMode: BigNumber.RoundingMode,
    decimalPlaces = 0
  ): number {
    if (denominator === 0) return 0;
    return new BigNumber(value).times(numerator).div(denominator).dp(decimalPlaces, roundingMode).toNumber();
  }

  // ------------------------------------------------------------------
  // protected static methods
  // ------------------------------------------------------------------
  /**
   * 引き数の端数処理を、BigNumberのroundingModeに変換して返します
   *
   * @param e端数処理
   * @returns
   */
  protected static getBigNumberRoundingMode(e端数処理: E端数処理): BigNumber.RoundingMode {
    let roundingMode: BigNumber.RoundingMode = BigNumber.ROUND_HALF_UP;
    switch (e端数処理) {
      case E端数処理.切上げ:
        roundingMode = BigNumber.ROUND_UP;
        break;
      case E端数処理.切捨て:
        roundingMode = BigNumber.ROUND_DOWN;
        break;
      case E端数処理.四捨五入:
        roundingMode = BigNumber.ROUND_HALF_UP;
        break;
    }
    return roundingMode;
  }

  // ------------------------------------------------------------------
  // public abstract methods
  // ------------------------------------------------------------------
  /**
   * データs から T を作成します
   *
   * @param データs
   * @param option
   * @returns
   */
  public abstract parse(
    データs: データListItemResponse[],
    option?: Record<string, string | number | true | AbstractデータService<unknown>>
  ): T;

  /**
   * データs から 帳票ListItemRequest を作成します
   *
   * @param データs
   * @param option
   * @returns
   */
  public abstract parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number | boolean | AbstractデータService<unknown>>
  ): 帳票データ | 帳票データ[];

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------
  /**
   * E入力分類s を取得します
   */
  public getE入力分類s(): E入力分類[] {
    return this.getE入力分類sByServiceType(this.serviceType, true);
  }

  /**
   * serviceに紐づくE入力分類s を取得します
   *
   * @returns
   */
  public getRelationalE入力分類s(): E入力分類[] {
    return _.uniq(this.getE入力分類sByServiceType(this.serviceType));
  }

  // ------------------------------------------------------------------
  // protected methods
  // ------------------------------------------------------------------
  /**
   * データs を　e入力分類　で絞り込みます
   *
   * @param データs
   * @param e入力分類s
   * @returns
   */
  protected 入力分類Filter(データs: データListItemResponse[], e入力分類s?: E入力分類[]): データListItemResponse[] {
    const 入力分類s = e入力分類s === undefined ? this.getE入力分類s() : e入力分類s;
    return データs.filter((d) => 入力分類s.includes(d.c入力分類));
  }

  /**
   * データs から自身の e入力分類 に一致するデータを取得します
   *
   * @param データs
   * @returns
   */
  protected findBy入力分類(データs: データListItemResponse[]): データListItemResponse | undefined {
    return データs.find((d) => this.getE入力分類s().includes(d.c入力分類));
  }

  /**
   * データs を　eフェーズ種別　で絞り込みます
   *
   * @param データs
   * @returns
   */
  protected フェーズ種別Filter(データs: データListItemResponse[]): データListItemResponse[] {
    return データs.filter((d) => d.cフェーズ種別 === this.eフェーズ種別);
  }

  /**
   * テンプレート行指定配列に偶奇テンプレート行インデックスを追加します
   *
   * @param templateRows
   * @param rowIndex
   * @param oddTemplate 奇数テンプレート行インデックス
   * @param evenTemplate 偶数テンプレート行インデックス
   */
  protected pushAlternateTemplateRow(
    templateRows: number[],
    rowIndex: number,
    oddTemplate: number,
    evenTemplate: number
  ): void {
    templateRows.push(rowIndex % 2 === 0 ? oddTemplate : evenTemplate);
  }

  /**
   * テンプレートセル値をそのまま表示するための null配列を取得します
   *
   * @param columnCount
   * @returns
   */
  protected templateCellValues(columnCount: number): null[] {
    return new Array(columnCount).fill(null);
  }

  /**
   * xlsx シート名としての禁則文字を削除し、更に30文字を超える場合は頭30時文字を取得します
   * ※シート名は31文字まで許容だが、ブック内で加工シート名が重複した場合に序数接尾辞をつけるための余白を残します
   *
   * @param name
   * @returns
   */
  protected createXlsxSheetName(name: string): string {
    // eslint-disable-next-line quotes
    const regex = new RegExp("[:\\\\\\?\\[ \\]\\/\\*'￥]*", 'g');
    return name.replace(regex, '').substring(0, 30);
  }

  /**
   * 引き数のシート名が重複した場合、序数接尾辞をつけます
   *
   * @param list
   */
  protected setIndexedXlsxSheetNames(list: 帳票データ[]): void {
    for (const checkItem of list) {
      if (checkItem.sheetName === undefined) continue;
      const filteredList = list.filter((item) => item.sheetName === checkItem.sheetName);
      if (filteredList.length > 1) filteredList.forEach((item, index) => (item.sheetName! += index));
    }
  }

  /**
   * 各端数処理をBigNumberのroundingModeに変換して格納します
   */
  protected setBnRoundingMode(): void {
    this.bnRoundingMode = {
      減価償却: this.getBigNumberRoundingMode(AbstractデータService.事業所.c減価償却費端数処理),
      消費税: this.getBigNumberRoundingMode(AbstractデータService.事業所.c消費税端数処理),
      実績計画: this.getBigNumberRoundingMode(AbstractデータService.事業所.c実績値及び計画値端数処理),
    };
  }

  /**
   * valueを引数の割合で換算し、指定の小数点桁数・端数処理モードで丸めた値を返します
   * (value × numerator / denominator)
   *
   * @param value
   * @param numerator
   * @param denominator
   * @param roundingMode
   * @param decimalPlaces
   * @returns
   */
  protected calcPercentByBigNumber(
    value: number,
    numerator: number,
    denominator: number,
    roundingMode: BigNumber.RoundingMode,
    decimalPlaces = 0
  ): number {
    return AbstractデータService.calcPercentByBigNumber(value, numerator, denominator, roundingMode, decimalPlaces);
  }

  /**
   * 経理方式により、入力値の消費税額を計算して返します
   *   税込経理: value × 消費税率 / (100 + 消費税率),
   *   税抜経理: value × 消費税率 / 100
   *
   * @param value
   * @returns
   */
  protected get消費税(value: number): number {
    const 消費税率 = AbstractデータService.事業所.c消費税率;
    const denominator = AbstractデータService.事業所.c経理方式 === E経理方式.税抜経理 ? 100 : 100 + 消費税率;
    return this.calcPercentByBigNumber(value, 消費税率, denominator, this.bnRoundingMode.消費税);
  }

  /**
   * 金額を帳票用にフォーマット
   * @param value
   * @returns
   */
  protected getFormatMoneyValue(value: number | null | undefined): string {
    // 円・千円の単位変換処理後、設定する
    return FormatUtility.toLocaleString(this.getMoneyUnitValue(value));
  }

  /**
   * 設定中の金額表示単位の値を取得します
   * @param value
   * @returns
   */
  protected getMoneyUnitValue(value: number | null | undefined): number {
    // 円・千円の単位変換して返却
    const unit = AbstractデータService.getMoneyUnit(AbstractデータService.計画書.c千円表示フラグ);
    return AppFormatUtility.changeUnit(value, unit);
  }

  protected convertByMoneyUnit(val: number | undefined) {
    return val === undefined ? '' : this.getMoneyUnitValue(val);
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------
  /**
   * 引き数の端数処理を、BigNumberのroundingModeに変換して返します
   *
   * @param e端数処理
   * @returns
   */
  private getBigNumberRoundingMode(e端数処理: E端数処理): BigNumber.RoundingMode {
    return AbstractデータService.getBigNumberRoundingMode(e端数処理);
  }

  /**
   * serviceTypeに紐づくE入力分類一覧を返却します
   *
   * @param serviceType
   * @param isWithoutChildren
   * @returns
   */
  private getE入力分類sByServiceType(serviceType: ServiceType, isWithoutChildren = false): E入力分類[] {
    // e入力分類s：そのserviceで必要な入力分類
    // children：そのserviceで呼び出す別service
    let e入力分類s: E入力分類[] = [];
    let children: ServiceType[] = [];
    switch (serviceType) {
      case ServiceType.分析Plbscf:
        e入力分類s = [E入力分類.PLBSCF];
        break;
      case ServiceType.金融機関借入:
        e入力分類s = [E入力分類.金融機関借入];
        children = [ServiceType.OldLoan];
        break;
      case ServiceType.役員借入:
        e入力分類s = [E入力分類.役員借入];
        children = [ServiceType.OldLoan];
        break;
      case ServiceType.法人保有不動産:
        e入力分類s = [E入力分類.法人保有不動産];
        break;
      case ServiceType.償却:
        e入力分類s = [E入力分類.償却];
        break;
      case ServiceType.実態BS:
        e入力分類s = [E入力分類.実態BS];
        children = [ServiceType.分析Plbscf, ServiceType.法人保有不動産, ServiceType.償却];
        break;
      case ServiceType.計画Plbscf:
        e入力分類s = [E入力分類.PLBSCF, E入力分類.科目別計画設定, E入力分類.YEARCONFIG];
        children = [ServiceType.分析Plbscf, ServiceType.償却, ServiceType.返済明細];
        break;
      case ServiceType.返済計画:
        e入力分類s = [E入力分類.返済計画];
        children = [ServiceType.計画Plbscf, ServiceType.金融機関借入];
        break;
      case ServiceType.返済明細:
        e入力分類s = [];
        children = [ServiceType.金融機関借入, ServiceType.役員借入];
        break;
      case ServiceType.納税予測:
        e入力分類s = [E入力分類.納税予測];
        children = [ServiceType.計画Plbscf, ServiceType.償却];
        break;
      case ServiceType.資金繰り:
        e入力分類s = [E入力分類.資金繰り];
        children = [ServiceType.計画Plbscf, ServiceType.返済明細];
        break;
      case ServiceType.実態純資産:
        e入力分類s = [E入力分類.実態純資産];
        children = [ServiceType.計画Plbscf, ServiceType.実態BS];
        break;
      case ServiceType.個人保有不動産:
        e入力分類s = [E入力分類.個人保有不動産];
        break;
      case ServiceType.差異分析:
        e入力分類s = [E入力分類.モニタリングPLBSCF, E入力分類.科目別計画設定];
        children = [ServiceType.計画Plbscf];
        break;
      case ServiceType.業績予測:
        e入力分類s = [E入力分類.モニタリングPLBSCF, E入力分類.科目別計画設定];
        children = [ServiceType.計画Plbscf];
        break;
      case ServiceType.OldLoan:
        e入力分類s = [E入力分類.OLDLOAN, E入力分類.役員借入, E入力分類.金融機関借入];
        break;
    }
    if (isWithoutChildren) return e入力分類s;

    let e入力分類sOfChildren: E入力分類[] = [];
    for (const childType of children)
      e入力分類sOfChildren = e入力分類sOfChildren.concat(this.getE入力分類sByServiceType(childType));
    return e入力分類s.concat(e入力分類sOfChildren);
  }
}
